import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button, Input, Fieldset, Legend, Text, Heading } from '../components/ui'
import { EyeIcon, EyeSlashIcon, ArrowLeftIcon } from '@heroicons/react/16/solid'
import { BRAND_COLOR } from '../constants'
import { useTheme } from '../hooks'
import { useAuthContext } from '../contexts/AuthContext'
import { testSupabaseConnection } from '../lib/supabase-test'
import { validateUserForm } from '../utils/userValidation'
import { PasswordStrengthMeter } from '../components/PasswordStrengthMeter'
import { debugEnvironmentVariables } from '../lib/debug-env'

const Register: React.FC = () => {
  const { darkMode } = useTheme()
  const navigate = useNavigate()
  const { signUp, loading, error, isAuthenticated } = useAuthContext()
  const [registerError, setRegisterError] = useState<string | null>(null)
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  })
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const [registrationCompleted, setRegistrationCompleted] = useState<boolean>(false)
  const [isTestingConnection, setIsTestingConnection] = useState(false)

  // Redirecionar usuários já logados
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard', { replace: true })
    }
  }, [isAuthenticated, navigate])

  // Verificar variáveis de ambiente ao carregar a página
  useEffect(() => {
    debugEnvironmentVariables()
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitError(null)

    // Verificar variáveis de ambiente antes de tentar registrar
    if (!process.env.REACT_APP_SUPABASE_URL || !process.env.REACT_APP_SUPABASE_ANON_KEY) {
      setSubmitError('Configuração do Supabase não encontrada. Verifique as variáveis de ambiente.')
      return
    }

    // Validação completa do formulário usando função utilitária
    const formValidation = validateUserForm({
      name: formData.name,
      email: formData.email,
      password: formData.password,
      confirmPassword: formData.confirmPassword,
      user_group_id: 'usuario' // Grupo padrão para registro público
    })

    if (!formValidation.isValid) {
      setSubmitError(formValidation.error!)
      return
    }

    try {
      const result = await signUp(formData.email, formData.password, formData.name)
      
      if (result.success) {
        // Exibir confirmação de envio de email/registro em vez de redirecionar
        setSuccessMessage('Conta criada! Enviamos um e-mail de confirmação. Verifique sua caixa de entrada e siga as instruções para ativar seu acesso.')
        setRegistrationCompleted(true)
      } else {
        setRegisterError(result.error || 'Erro ao registrar usuário')
        setSubmitError(result.error || 'Erro ao registrar usuário')
      }
    } catch (error) {
      setSubmitError('Erro inesperado. Tente novamente.')
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleBackToLogin = () => {
    navigate('/login')
  }

  const handleTestConnection = async () => {
    setIsTestingConnection(true)
    try {
      const result = await testSupabaseConnection()
      if (result.success) {
        alert('✅ Conexão com Supabase OK!')
      } else {
        alert(`❌ Erro na conexão: ${result.error}`)
      }
    } catch (error) {
      alert(`❌ Erro inesperado: ${error}`)
    } finally {
      setIsTestingConnection(false)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center">
          <img 
            src="/logo-pasi.svg" 
            alt="PASI Logo" 
            className="w-12 h-12 rounded-lg object-contain"
          />
        </div>
        <Heading as="h2" size="2xl" className="mt-6 text-center">
          Criar conta
        </Heading>
        <Text size="sm" color="muted" className="mt-2 text-center">
          Registre-se na plataforma PasiAI
        </Text>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white dark:bg-gray-800 py-8 px-4 shadow sm:rounded-lg sm:px-10">
          {(registerError || submitError) && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
              <strong>Erro:</strong> {submitError || registerError}
              <div className="mt-2 space-y-2">
                <button
                  onClick={handleTestConnection}
                  disabled={isTestingConnection}
                  className="text-sm underline hover:no-underline block"
                >
                  {isTestingConnection ? 'Testando...' : 'Testar conexão com Supabase'}
                </button>
              </div>
            </div>
          )}

          {successMessage && (
            <div className="mb-6 p-3 bg-green-50 border border-green-400 text-green-800 rounded">
              <strong>Sucesso:</strong> {successMessage}
            </div>
          )}
          
          {!registrationCompleted ? (
            <form className="space-y-6" onSubmit={handleSubmit}>
            <Fieldset>
              <Legend>Nome completo</Legend>
              <div className="mt-2">
                <Input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Digite seu nome completo"
                  required
                  disabled={loading}
                />
              </div>
            </Fieldset>

            <Fieldset>
              <Legend>Endereço de email</Legend>
              <div className="mt-2">
                <Input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="Digite seu email"
                  required
                  disabled={loading}
                />
              </div>
            </Fieldset>

            <Fieldset>
              <Legend>Senha</Legend>
              <div className="mt-2 relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  placeholder="Digite sua senha"
                  required
                  disabled={loading}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                  disabled={loading}
                >
                  {showPassword ? (
                    <EyeSlashIcon className="h-4 w-4 text-gray-400" />
                  ) : (
                    <EyeIcon className="h-4 w-4 text-gray-400" />
                  )}
                </button>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Mínimo de 6 caracteres.</p>
              <PasswordStrengthMeter password={formData.password} />
            </Fieldset>

            <Fieldset>
              <Legend>Confirmar senha</Legend>
              <div className="mt-2 relative">
                <Input
                  type={showConfirmPassword ? 'text' : 'password'}
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  placeholder="Confirme sua senha"
                  required
                  disabled={loading}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  aria-label={showConfirmPassword ? 'Ocultar senha' : 'Mostrar senha'}
                  disabled={loading}
                >
                  {showConfirmPassword ? (
                    <EyeSlashIcon className="h-4 w-4 text-gray-400" />
                  ) : (
                    <EyeIcon className="h-4 w-4 text-gray-400" />
                  )}
                </button>
              </div>
            </Fieldset>

            <div>
              <Button 
                type="submit" 
                className="w-full text-white" 
                size="lg"
                style={{ backgroundColor: BRAND_COLOR }}
                onMouseEnter={(e: React.MouseEvent<HTMLButtonElement>) => {
                  e.currentTarget.style.backgroundColor = '#006d75'
                }}
                onMouseLeave={(e: React.MouseEvent<HTMLButtonElement>) => {
                  e.currentTarget.style.backgroundColor = BRAND_COLOR
                }}
                disabled={loading}
              >
                {loading ? 'Criando conta...' : 'Criar conta'}
              </Button>
            </div>

            <div className="text-center">
              <Text size="sm" color="muted">
                Já tem uma conta?{' '}
                <button 
                  type="button"
                  onClick={handleBackToLogin}
                  className="font-medium"
                  style={{ color: BRAND_COLOR }}
                  onMouseEnter={(e: React.MouseEvent<HTMLButtonElement>) => {
                    e.currentTarget.style.color = '#006d75'
                  }}
                  onMouseLeave={(e: React.MouseEvent<HTMLButtonElement>) => {
                    e.currentTarget.style.color = BRAND_COLOR
                  }}
                  disabled={loading}
                >
                  Faça login
                </button>
              </Text>
            </div>
          </form>
          ) : (
            <div className="space-y-6">
              <Button
                type="button"
                className="w-full text-white"
                size="lg"
                style={{ backgroundColor: BRAND_COLOR }}
                onClick={handleBackToLogin}
              >
                Ir para login
              </Button>
              <Text size="sm" color="muted" className="text-center">
                Se não encontrar o e-mail, verifique a caixa de spam ou aguarde alguns minutos.
              </Text>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default Register
